import React, { useEffect, useRef, useState } from "react";
import {
  createEmbeddingContext,
  EmbeddingContext,
  FrameOptions,
} from "amazon-quicksight-embedding-sdk";
import GraphqlService from "../../../service/graphqlService";


interface QuickSightDashboardProps {
  id?: string;
  projectId?: string | number | (string | number)[];
}


const QuickSightDashboard: React.FC<QuickSightDashboardProps> = ({ id, projectId }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const dashboardId: string = id || "default";
  const embeddingContextRef = useRef<EmbeddingContext | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);

  // Convert projectId to string of comma-separated codes
  let projectCodes = '';
  if (Array.isArray(projectId)) {
    projectCodes = projectId.join(',');
  } else if (typeof projectId === 'string' || typeof projectId === 'number') {
    projectCodes = String(projectId);
  }

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setHasError(false);

    const loadDashboard = async () => {
      // Clear previous dashboard if exists
      if (embeddingContextRef.current && containerRef.current) {
        try {
          // Clear the container content
          containerRef.current.innerHTML = '';
        } catch (error) {
          console.warn('Error clearing previous dashboard:', error);
        }
      }
      try {
        // Pass all codes as comma-separated string
        const response = await GraphqlService.getAnonymousQuickSightUrl(
          "default",
          dashboardId,
          projectCodes
        );

        const embedUrl: string | undefined = response?.url;

        // Extract authCode from the URL
        const urlParams = new URLSearchParams(embedUrl?.split("?")[1]);
        const authCode: string | null = urlParams.get("code");

        if (!embedUrl) {
          console.error("No embed URL returned from the API");
          if (isMounted) setHasError(true);
          return;
        }

        if (!authCode) {
          console.error("Authorization code is missing or invalid");
          if (isMounted) setHasError(true);
          return;
        }

        if (!containerRef.current) {
          console.error("Dashboard container ref is not available");
          if (isMounted) setHasError(true);
          return;
        }

        const embeddingContext: EmbeddingContext = await createEmbeddingContext();

        // Store the embedding context for cleanup
        embeddingContextRef.current = embeddingContext;

        const frameOptions: FrameOptions = {
          url: embedUrl,
          container: containerRef.current,
          height: "700px",
          width: "100%",
          resizeHeightOnSizeChangedEvent: true,
          onChange: (changeEvent: any) => {
            // Hide skeleton once the iframe has finished loading
            if (changeEvent?.eventName === 'FRAME_LOADED' && isMounted) {
              setIsLoading(false);
            }
          },
        };



        await embeddingContext.embedDashboard(frameOptions);
        console.log("Dashboard embedding initialized");
        // Safety net: if FRAME_LOADED never arrives, hide skeleton anyway
        if (isMounted) setIsLoading(false);
      } catch (error) {
        console.error("Error embedding dashboard:", error);
        if (isMounted) {
          setHasError(true);
          setIsLoading(false);
        }
      }
    };

    loadDashboard();

    // Cleanup function
    return () => {
      isMounted = false;
      if (embeddingContextRef.current && containerRef.current) {
        try {
          // Clear the container content
          containerRef.current.innerHTML = '';
        } catch (error) {
          console.warn('Error during cleanup:', error);
        }
      }
    };
  }, [dashboardId, projectCodes]);

  return (
    <div style={{ position: "relative", height: "700px", width: "100%" }}>
      <div ref={containerRef} style={{ height: "700px", width: "100%" }} />
      {(isLoading || hasError) && (
        <div
          style={{ position: "absolute", inset: 0, pointerEvents: hasError ? "auto" : "none", zIndex: 10 }}
          className="bg-white"
          aria-live="polite"
          aria-busy={isLoading}
        >
          {hasError ? (
            <div className="flex items-center justify-center h-full">
              <p className="text-sm text-gray-500">Unable to load dashboard. Please try again later.</p>
            </div>
          ) : (
            <DashboardSkeleton />
          )}
        </div>
      )}
    </div>
  );
};

const shimmerStyle: React.CSSProperties = {
  background:
    "linear-gradient(90deg, #f3f4f6 0%, #e5e7eb 40%, #f9fafb 50%, #e5e7eb 60%, #f3f4f6 100%)",
  backgroundSize: "200% 100%",
  animation: "qsShimmer 1.4s ease-in-out infinite",
};

const DashboardSkeleton: React.FC = () => (
  <div className="w-full p-4 md:p-5 flex flex-col gap-3">
    <style>{`@keyframes qsShimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>

    {/* Header row: title + KPI chips */}
    <div className="flex items-center justify-between gap-4">
      <div className="h-4 w-40 rounded" style={shimmerStyle} />
      <div className="hidden md:flex gap-2">
        <div className="h-6 w-16 rounded-full" style={shimmerStyle} />
        <div className="h-6 w-16 rounded-full" style={shimmerStyle} />
        <div className="h-6 w-16 rounded-full" style={shimmerStyle} />
      </div>
    </div>

    {/* Main row: donut + map (bounded height to fit clipped wrapper) */}
    <div className="flex gap-3 md:gap-4 h-[220px] md:h-[260px]">
      {/* Donut card */}
      <div className="flex-1 rounded-xl border border-gray-100 bg-white p-3 flex flex-col items-center justify-center gap-2 shadow-sm">
        <div className="relative w-20 h-20 md:w-28 md:h-28">
          <div className="absolute inset-0 rounded-full" style={shimmerStyle} />
          <div className="absolute inset-3 rounded-full bg-white" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="h-3 w-10 rounded" style={shimmerStyle} />
          </div>
        </div>
        <div className="h-2.5 w-20 rounded" style={shimmerStyle} />
        <div className="flex gap-2">
          <div className="h-2 w-10 rounded" style={shimmerStyle} />
          <div className="h-2 w-10 rounded" style={shimmerStyle} />
        </div>
      </div>

      {/* Map card */}
      <div className="flex-[2] rounded-xl border border-gray-100 bg-white p-3 shadow-sm flex flex-col gap-2">
        <div className="h-3 w-32 rounded" style={shimmerStyle} />
        <div className="flex-1 rounded-lg relative overflow-hidden" style={shimmerStyle}>
          <span className="absolute w-2.5 h-2.5 rounded-full bg-blue-300/70 animate-pulse" style={{ top: "30%", left: "20%" }} />
          <span className="absolute w-2 h-2 rounded-full bg-blue-300/70 animate-pulse" style={{ top: "45%", left: "55%" }} />
          <span className="absolute w-3 h-3 rounded-full bg-indigo-300/70 animate-pulse" style={{ top: "60%", left: "35%" }} />
          <span className="absolute w-2 h-2 rounded-full bg-blue-300/70 animate-pulse" style={{ top: "25%", left: "70%" }} />
          <span className="absolute w-2.5 h-2.5 rounded-full bg-indigo-300/70 animate-pulse" style={{ top: "70%", left: "80%" }} />
        </div>
      </div>
    </div>

    <div className="flex items-center justify-center gap-2">
      <svg className="w-4 h-4 text-indigo-500 animate-spin" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
      </svg>
      <p className="text-xs text-gray-500">Loading dashboard…</p>
    </div>
  </div>
);

export default QuickSightDashboard;
